import { getCreationDetail, getMotionDetail } from '@/app/apis';
import type { FormImagePrompt, ToolFormParams, ToolTaskFormVideoPrompt } from '@/app/types';

const isReferenceUnavailable = (error?: { code?: number; reason?: string } | null) => {
  return !!error;
};

const canSubmitImageByUrl = (prompt: FormImagePrompt) => {
  return !!(prompt.prompt?.selectedImage || prompt.prompt?.sourceImage);
};

const canSubmitVideoByUrl = (prompt: ToolTaskFormVideoPrompt) => {
  return !!prompt.prompt?.video;
};

const sanitizeCreationReference = async (creationId?: string) => {
  if (!creationId || creationId === '0') {
    return true;
  }

  try {
    const { error } = await getCreationDetail({
      path: { id: creationId },
    });
    return !isReferenceUnavailable(error);
  } catch {
    return true;
  }
};

const sanitizeMotionReference = async (motionId?: string) => {
  if (!motionId) {
    return true;
  }

  try {
    const { error } = await getMotionDetail({
      path: { id: motionId },
    });
    return !isReferenceUnavailable(error);
  } catch {
    return true;
  }
};

const sanitizeImagePrompt = async (prompt: FormImagePrompt): Promise<FormImagePrompt> => {
  const creationId = prompt.reference?.creation_id;
  if (!creationId) {
    return prompt;
  }

  if (!(await sanitizeCreationReference(creationId)) && canSubmitImageByUrl(prompt)) {
    return {
      ...prompt,
      id: prompt.prompt?.selectedImage || prompt.prompt?.sourceImage || prompt.id,
      reference: undefined,
    };
  }

  return prompt;
};

const sanitizeVideoPrompt = async (prompt: ToolTaskFormVideoPrompt): Promise<ToolTaskFormVideoPrompt> => {
  const creationId = prompt.reference?.creation_id;
  const motionId = prompt.reference?.motion_id;
  if (!creationId && !motionId) {
    return prompt;
  }

  const isCreationAvailable = await sanitizeCreationReference(creationId);
  const isMotionAvailable = await sanitizeMotionReference(motionId);

  if (((creationId && !isCreationAvailable) || (motionId && !isMotionAvailable)) && canSubmitVideoByUrl(prompt)) {
    const { creation_id: _creationId, motion_id: _motionId, ...reference } = prompt.reference || {};
    const nextReference = Object.keys(reference).length > 0 ? reference : undefined;

    return {
      ...prompt,
      id: prompt.prompt?.video || prompt.id,
      reference: nextReference,
    };
  }

  return prompt;
};

export const sanitizeToolRecreatePatchReferences = async (
  patch: Partial<ToolFormParams>,
): Promise<Partial<ToolFormParams>> => {
  const [imagePrompts, videoPrompts] = await Promise.all([
    patch.imagePrompts ? Promise.all(patch.imagePrompts.map(sanitizeImagePrompt)) : Promise.resolve(undefined),
    patch.videoPrompts ? Promise.all(patch.videoPrompts.map(sanitizeVideoPrompt)) : Promise.resolve(undefined),
  ]);

  return {
    ...patch,
    imagePrompts,
    videoPrompts,
    creationId:
      patch.creationId === undefined
        ? undefined
        : patch.creationId !== '0' && (await sanitizeCreationReference(patch.creationId))
          ? patch.creationId
          : undefined,
  };
};
